// =============================================================================
// Organ druku (Część K) — kontrakt drutowy v2 print-core.
//
// To jest JEDYNE miejsce, w którym kształt ciał `POST /api/print/jobs` i
// `GET /api/print/jobs/:id` istnieje po stronie TypeScriptu — czytają go
// print-core (serwer), @insolitum/print-client (moduły i serwisy) oraz testy
// kontraktowe. Pola są w snake_case, bo to typy DRUTU, nie modelu domenowego:
// dokładnie te bajty idą w JSON-ie i dokładnie po tych nazwach czyta je serwer.
//
// Reguły kontraktu, których nie widać w samych typach (wszystkie pola są
// opcjonalne, żeby ciało v1 pozostało poprawne na zawsze — zmiana addytywna):
//
//   1. XOR planów: zadanie wskazuje ALBO wzór (`template_slug` + `variables`),
//      ALBO gotowe bajty (`payload` | `payload_url`) — nigdy oba naraz i nigdy
//      żadnego. Oba plany naraz = dwa źródła prawdy o treści wydruku; żaden =
//      zadanie bez treści. print-client odrzuca to lokalnie, print-core
//      odrzuca to samo 400-tką (obrona w głębi, nie duplikacja).
//
//   2. `format` NIGDY nie przychodzi od klienta na planie wzoru — jest pochodną
//      `target_format` wzoru × `protocol` rozwiązanej drukarki (decyzja z planu
//      §6.1: druk na sterowniku Windows to `format='PDF'` + drukarka `system`,
//      a moduł o tym nie wie i nie dba). Dlatego tego pola tu NIE MA.
//
//   3. Rozwiązywanie drukarki: jawny `printer_id` wygrywa; bez niego `purpose`
//      (+ opcjonalnie `module`, `site_id`) idzie przez `print_printer_assignments`
//      od najściślejszego dopasowania do `(org, purpose)`. Brak dopasowania to
//      twarde 422 `no_printer_for_purpose` — nigdy cichy fallback (lekcja
//      PackFlow: domyślna drukarka „jakaś" drukuje śmieci gdzieś).
// =============================================================================

/**
 * Zamknięty słownik ról drukarek (D7). Kolumna DB pozostaje TEXT — słownika
 * pilnuje ten typ i test, nie CHECK, żeby dopisanie roli było zmianą w jednym
 * pliku, a nie migracją. Rozszerzenia wyłącznie przez dopisanie tutaj.
 */
export const PRINT_PURPOSES = [
  'packing_list',
  'product_label',
  'shipping_label',
  'package_label',
  'invoice',
  'wz',
  'test_page',
] as const;

export type PrintPurpose = (typeof PRINT_PURPOSES)[number];

/**
 * Stany zadania druku — lustro enuma `print_job_status` (migracja 186).
 * `cancelled` osiągalne WYŁĄCZNIE ze stanu `queued` (zadania przejętego przez
 * agenta nie da się odwołać — papier może już istnieć).
 */
export type PrintJobStatus = 'queued' | 'picked' | 'printing' | 'done' | 'error' | 'cancelled';

/**
 * Ślad etykiety (migracja 550) — wskaźnik na rzecz z produkcji. Słownik kluczy
 * jest ZAMKNIĘTY; bramką wejścia i kanonem walidacji pozostaje
 * `services/print-core/src/slad.ts` (ten typ tylko odwzorowuje jego kształt
 * na drucie, żeby klient nie przemycił literówki w nazwie klucza).
 */
export type SladDruku = Partial<
  Record<'production_order_line_id' | 'batch_id' | 'production_stage_id', string>
>;

/**
 * Ciało `POST /api/print/jobs` v2 — w pełni addytywne rozszerzenie v1.
 * Ciało v1 (`document_type` + `payload`) pozostaje poprawne bez zmian.
 */
export interface PrintJobRequestV2 {
  // ── Plan wzoru (XOR z payload/payload_url — reguła 1 w nagłówku) ────────────
  /** Slug wzoru z katalogu `packages/print-templates`; render robi CHMURA (D5). */
  template_slug?: string;
  /** Zmienne wzoru. Braki nieopcjonalnych zmiennych odrzuca render, nie agent. */
  variables?: Record<string, unknown>;

  // ── Plan roli (używany, gdy brak jawnego printer_id — reguła 3) ─────────────
  /** Rola drukarki — moduł mówi CO drukuje, nie NA CZYM (D7). */
  purpose?: PrintPurpose;
  /** Slug modułu wołającego — zawęża dopasowanie przypisań. */
  module?: string;
  /** Lokalizacja (`organization_sites.id`) — opcjonalne zawężenie przypisań. */
  site_id?: string;

  // ── Idempotencja (D13) ──────────────────────────────────────────────────────
  /**
   * Klucz idempotencji per organizacja. Powtórne zlecenie z tym samym kluczem
   * (retry kuriera po słabym Wi-Fi, redelivery konsumenta at-least-once) NIE
   * tworzy drugiego zadania — serwer odpowiada istniejącym zadaniem z flagą
   * `idempotent_replay`. Bez klucza retry po stronie klienta jest ZABRONIONE
   * (ryzyko fizycznego zdublowania etykiety).
   */
  client_job_key?: string;

  // ── Pola v1 — istniejące, bez zmian ─────────────────────────────────────────
  /** Typ dokumentu (wolny tekst). Na planie wzoru serwer wypełnia go slugiem. */
  document_type?: string;
  /** Gotowe bajty dla drukarki (etykiety termiczne) — plan bajtów. */
  payload?: string;
  /** Podpisany URL artefaktu (duże PDF-y) — plan bajtów. */
  payload_url?: string;
  /** Jawna drukarka — ścieżka admina/testów; wygrywa z `purpose`. */
  printer_id?: string;
  /** Odstępstwo tenanta od wzoru (wiersz `print_templates`). */
  template_id?: string;
  copies?: number;
  /** Podmiot etykiety — patrz `SladDruku` i `services/print-core/src/slad.ts`. */
  slad?: SladDruku;
}

/**
 * Odpowiedź `POST /api/print/jobs`: 201 przy nowym zadaniu, 200 z
 * `idempotent_replay: true` gdy `client_job_key` trafił w istniejące zadanie
 * (unikalny indeks / 23505 obsługuje SERWER — klient tylko czyta flagę i nie
 * traktuje powtórki jak błędu).
 */
export interface PrintJobUtworzone {
  id: string;
  status: PrintJobStatus;
  /** `true` = to zadanie już istniało (dedup po `client_job_key`), nic nowego nie powstało. */
  idempotent_replay?: boolean;
}

/**
 * Odpowiedź `GET /api/print/jobs/:id` — status dla pollerów i `watchJob`.
 * Celowo wąski przekrój wiersza `print_jobs`: bez `payload` (bajty nie wracają
 * do klienta) i bez pól toru agenta.
 */
export interface PrintJobStatusOdpowiedz {
  id: string;
  status: PrintJobStatus;
  /** Komunikat błędu agenta / dead-letter — tylko dla `status='error'`. */
  error?: string | null;
  printed_at?: string | null;
  created_at: string;
  /** Rozwiązana drukarka (po planie roli może różnić się od intencji klienta). */
  printer_id?: string | null;
  attempts?: number;
}
